import React, { Component } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import identity from 'lodash/identity';
import memoize from 'lodash/memoize';
import cond from 'lodash/cond';
import constant from 'lodash/constant';
import stubTrue from 'lodash/stubTrue';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { SuiContainer, SuiButton } from '@splashthat/sui';
import { withFlags } from 'utils/react-context';

import DotDragGripIcon from 'common/icons/DotDragGrip';
import LockIcon from 'common/icons/Lock';
import PencilIcon from 'common/icons/Pencil';
import { EDITABLE_LOGIC_PROPERTIES, LOGIC_PROPERTY_REQUIRED } from 'common/entities/formLogicRule/constants';
import { getFormNodeEditingLocked } from 'common/entities/formNode/selectors';
import { getFormTabHideAddQuestionButton } from 'common/store/userProperties/selectors';
import { getCanUserDeleteRegistrationForm } from 'common/store/permissions';
import { getCanUserUpdateLibraryFormsUserPermission } from 'common/store/userPermissions/selectors';
import {
    NODE_TYPE_ROOT,
    NODE_TYPE_GROUP,
    NODE_TYPE_QUESTION,
    NODE_TYPE_CONTENT,
    NODE_TYPE_TICKET_SELECTION,
    NODE_TYPE_QUESTION_MULTIPLE,
} from 'common/entities/formNode/nodeTypes';

import FixedControls from './FixedControls';

import FormEditorNodeDropzone from './FormEditorNodeDropzone';
import FormEditorNodeControls from './FormEditorNodeControls';
import LayoutNodeLabel from './LayoutNodeLabel';
import { FormBuilderContext } from '../FormBuilder/FormBuilder';
import LayoutNodeContentContainer from './LayoutNodeContentContainer';

import { createTextElementContentNode } from '../../actions/nodeActions';
import { navigateToTicketEditor } from '../../actions';

import {
    EDIT_LOCKED_NODE_NAMES,
    DUP_LOCKED_NODE_NAMES,
    DELETE_LOCKED_NODE_NAMES,
    DRAG_LOCKED_NODE_NAMES,
    ADD_TEMPLATE_NODE_NAMES,
    ADD_TEMPLATE_ADDITIONAL,
    VISIBILITY_LOCKED_NODE_NAMES,
} from '../../constants/nodeNameRules';

import { GROUP_LEVEL } from '../../constants/templateLibrary';

import { isAllowedLogicNode, getNodeLockingAllowed } from '../../selectors';
import { TICKET_SELECTION_QA } from '../../constants/ticketDataQa';

const DEFAULT_ROOT_LABEL = '(Root)';
const DEFAULT_CONTENT_LABEL = '(Untitled)';
const DEFAULT_CONTENT_TYPE_GROUP = 'Question Group';
const DEFAULT_CONTENT_TYPE_QUESTION = 'Question';
const DEFAULT_CONTENT_TYPE_CONTENT = 'Content';
const DEFAULT_TICKET_SELECTION_LABEL = 'Ticket Editor';
const DEFAULT_TICKET_SELECTION_SUBTITLE = 'Return to Ticket Editor';
const BASE_CLASS_NAME = 'LayoutNodeContent';

const isDragLockedByNodeName = memoize((name) => DRAG_LOCKED_NODE_NAMES.includes(name));
const isEditLockedByNodeName = memoize((name) => EDIT_LOCKED_NODE_NAMES.includes(name));
const isDuplicateLockedByNodeName = memoize((name) => DUP_LOCKED_NODE_NAMES.includes(name));
const isDeleteLockedByNodeName = memoize((name) => DELETE_LOCKED_NODE_NAMES.includes(name));
const isAddingNodesAllowedByNodeName = memoize((name) => ADD_TEMPLATE_NODE_NAMES.includes(name));
const isAdditionalNode = memoize((name) => ADD_TEMPLATE_ADDITIONAL.includes(name));
const isAllowedNodeVisibilityLogic = memoize((name) => !VISIBILITY_LOCKED_NODE_NAMES.includes(name));

const NODE_STATUS = {
    DISABLED: 'DISABLED',
    default: 'default',
};
export class LayoutNodeContentComponent extends Component {
    static propTypes = {
        formNode: PropTypes.object.isRequired,
        formLogicRules: PropTypes.array.isRequired,
        reservedNodeName: PropTypes.string,
        bootstrapInputTypeDisplayNames: PropTypes.object.isRequired,
        depth: PropTypes.number.isRequired,

        onActiveChange: PropTypes.func.isRequired,
        isActive: PropTypes.bool.isRequired,

        canUserDeleteRegistrationForm: PropTypes.bool.isRequired,
        canUserEditLibraryForms: PropTypes.bool.isRequired,
        editingLocked: PropTypes.bool.isRequired,
        nodeLockingAllowed: PropTypes.bool.isRequired,
        isAllowedLogicNode: PropTypes.bool.isRequired,
        hideAddQuestionButton: PropTypes.bool.isRequired,
        createTextElementContentNode: PropTypes.func,
        navigateToTicketEditor: PropTypes.func.isRequired,

        // TODO: Connect functions
        onClickEdit: PropTypes.func,
        onClickLogic: PropTypes.func,
        onClickDuplicate: PropTypes.func,
        onClickDelete: PropTypes.func,
        onClickAddTemplate: PropTypes.func,

        connectDragSource: PropTypes.func,

        flags: PropTypes.object.isRequired,
    };

    static defaultProps = {
        reservedNodeName: null,
        connectDragSource: null,
        onClickEdit: null,
        onClickLogic: null,
        onClickDuplicate: null,
        onClickDelete: null,
        onClickAddTemplate: null,
        createTextElementContentNode: null,
    };

    handleMouseEnter = () => {
        this.props.onActiveChange(true);
    };

    handleMouseLeave = () => {
        this.props.onActiveChange(false);
    };

    /**
     * Enables the active flag when a control is focused
     */
    handleFocusControl = () => {
        this.props.onActiveChange(true);
    };

    /**
     * Disables the active flag when a control is focused
     */
    handleBlurControl = () => {
        this.props.onActiveChange(false);
    };

    handleClickEdit = (event) => {
        if (this.getCanEditNode() && this.props.onClickEdit) {
            event.stopPropagation();
            this.props.onClickEdit();
        }
    };

    handleClickLogic = (event) => {
        if (isAllowedNodeVisibilityLogic(this.props.formNode.name) && this.props.onClickLogic) {
            event.stopPropagation();
            this.props.onClickLogic();
        }
    };

    handleClickDuplicate = (event) => {
        if (this.getCanDuplicateNode() && this.props.onClickDuplicate) {
            event.stopPropagation();
            this.props.onClickDuplicate();
        }
    };

    handleClickAddTemplate = (event) => {
        event.stopPropagation();
        const isAdditional = isAdditionalNode(this.props.formNode.name);
        this.props.onClickAddTemplate(GROUP_LEVEL, isAdditional, this.props.formNode.id);
    };

    handleClickAddTextElement = (event) => {
        event.stopPropagation();
        this.props.createTextElementContentNode(this.props.formNode.id);
    };

    handleClickTicketSelection = (event) => {
        event.stopPropagation();
        this.props.navigateToTicketEditor();
    };

    /**
     * Get the label for question nodes.
     */
    getQuestionNodeLabel = () => this.props.formNode.label || DEFAULT_CONTENT_LABEL;

    /**
     * Gets the label string
     *
     * @return {string}
     */
    getContentLabel() {
        const { formNode } = this.props;
        if (formNode.type === NODE_TYPE_ROOT) {
            return formNode.label || DEFAULT_ROOT_LABEL;
        }
        if (formNode.type === NODE_TYPE_CONTENT) {
            return formNode.description || DEFAULT_CONTENT_LABEL;
        }
        if (formNode.type === NODE_TYPE_TICKET_SELECTION) {
            return DEFAULT_TICKET_SELECTION_LABEL;
        }
        return this.getQuestionNodeLabel();
    }

    /**
     * Gets the type string, if it has one
     *
     * @return {string|null}
     */
    getContentType() {
        const { formNode, bootstrapInputTypeDisplayNames } = this.props;

        switch (formNode.type) {
            case NODE_TYPE_ROOT: {
                return null;
            }
            case NODE_TYPE_GROUP: {
                return DEFAULT_CONTENT_TYPE_GROUP;
            }
            case NODE_TYPE_QUESTION: {
                return bootstrapInputTypeDisplayNames[formNode.inputType] || DEFAULT_CONTENT_TYPE_QUESTION;
            }
            case NODE_TYPE_QUESTION_MULTIPLE: {
                return DEFAULT_CONTENT_TYPE_QUESTION;
            }
            case NODE_TYPE_CONTENT: {
                return DEFAULT_CONTENT_TYPE_CONTENT;
            }
            default: {
                return null;
            }
        }
    }

    getIsInvisible() {
        return this.props.formNode.type === NODE_TYPE_ROOT;
    }

    getCanDrag() {
        return this.props.connectDragSource && !isDragLockedByNodeName(this.props.formNode.name);
    }

    getCanEditNode() {
        return (
            Boolean(this.props.onClickEdit) &&
            (!this.props.editingLocked || this.props.nodeLockingAllowed) &&
            !isEditLockedByNodeName(this.props.formNode.name) &&
            NODE_TYPE_TICKET_SELECTION !== this.props.formNode.type
        );
    }

    getCanDuplicateNode() {
        return Boolean(this.props.onClickDuplicate) && !isDuplicateLockedByNodeName(this.props.formNode.name);
    }

    getCanDeleteNode() {
        return Boolean(this.props.onClickDelete) && !isDeleteLockedByNodeName(this.props.formNode.name);
    }

    getCanAddTemplate() {
        return Boolean(this.props.onClickAddTemplate) && isAddingNodesAllowedByNodeName(this.props.formNode.name);
    }

    render() {
        if (this.getIsInvisible()) {
            return null;
        }

        const {
            canUserDeleteRegistrationForm,
            canUserEditLibraryForms,
            formNode,
            formLogicRules,
            connectDragSource,
            onClickDelete,
            isActive,
            reservedNodeName,
            editingLocked,
            nodeLockingAllowed,
            hideAddQuestionButton,
        } = this.props;

        const contentType = this.getContentType();
        const contentLabel = this.getContentLabel();

        const hasLogic = !!find(
            formLogicRules,
            (logicRule) => EDITABLE_LOGIC_PROPERTIES.indexOf(logicRule.property) !== -1,
        );
        const isRequired = !!find(
            formLogicRules,
            (logicRule) => logicRule.property === LOGIC_PROPERTY_REQUIRED && logicRule.defaultValue === true,
        );

        const canDrag = this.getCanDrag();
        const canEdit = this.getCanEditNode();

        const nodeLabelTypeProps = {
            [NODE_TYPE_TICKET_SELECTION]: {
                subtitle: DEFAULT_TICKET_SELECTION_SUBTITLE,
                onClick: this.handleClickTicketSelection,
                qaId: TICKET_SELECTION_QA,
            },
            default: {
                subtitle: reservedNodeName || contentType,
                onClick: canEdit ? this.handleClickEdit : undefined,
            },
        };

        const nodeControlTypeProps = {
            [NODE_TYPE_TICKET_SELECTION]: {
                onClickTicketSelection: this.handleClickTicketSelection,
            },
            default: {
                onClickAddTextElement: this.handleClickAddTextElement,
                onClickAddTemplate: this.getCanAddTemplate() ? this.handleClickAddTemplate : null,
                onClickLogic: isAllowedNodeVisibilityLogic(formNode.name) ? this.handleClickLogic : null,
                onClickDuplicate: this.getCanDuplicateNode() ? this.handleClickDuplicate : null,
                onClickDelete: this.getCanDeleteNode() ? onClickDelete : null,
            },
        };

        const nodeType = formNode.type;

        const status = cond([
            [
                constant(!this.props.flags.hasAccessToFormProtectedFields && formNode.protected),
                constant(NODE_STATUS.DISABLED),
            ],
            [stubTrue, constant(NODE_STATUS.default)],
        ])();

        return (
            <FormBuilderContext.Consumer>
                {({ permissions: { canUserEditForm: canUserAccess } }) => (
                    <LayoutNodeContentContainer
                        depth={this.props.depth}
                        isActive={this.props.isActive}
                        canEdit={status !== NODE_STATUS.DISABLED && canEdit}
                        onMouseEnter={this.handleMouseEnter}
                        onMouseLeave={this.handleMouseLeave}
                        onClick={status === NODE_STATUS.DISABLED ? () => {} : this.handleClickEdit}
                    >
                        <FormEditorNodeDropzone isHidden={!formNode.isDropzone} />

                        {canDrag &&
                            (connectDragSource || identity)(
                                <div className={['SuiContainer', `${BASE_CLASS_NAME}__icon-wrap`].join(' ')}>
                                    <SuiButton
                                        tagName="div"
                                        draggable="true"
                                        className={`${BASE_CLASS_NAME}__drag-grip`}
                                        variation="navigation"
                                        size="small"
                                        icon={DotDragGripIcon}
                                        tabIndex="-1"
                                    />
                                </div>,
                            )}

                        {canUserAccess && !canDrag && canEdit && (
                            <SuiContainer className={`${BASE_CLASS_NAME}__icon-wrap`}>
                                <SuiButton
                                    className={`${BASE_CLASS_NAME}__dead-icon`}
                                    variation="navigation"
                                    size="small"
                                    icon={canEdit ? PencilIcon : LockIcon}
                                    tabIndex="-1"
                                />
                            </SuiContainer>
                        )}

                        {
                            {
                                [NODE_STATUS.DISABLED]: (
                                    <>
                                        <LayoutNodeLabel
                                            isRequired={isRequired}
                                            isFaded
                                            disabled
                                            renderLabelPopover
                                            {...(nodeLabelTypeProps[nodeType] || nodeLabelTypeProps.default)}
                                        >
                                            {contentLabel}
                                        </LayoutNodeLabel>
                                        <SuiContainer full-height>
                                            <FixedControls />
                                        </SuiContainer>
                                    </>
                                ),
                                [NODE_STATUS.default]: (
                                    <>
                                        <LayoutNodeLabel
                                            isRequired={isRequired}
                                            isFaded={editingLocked}
                                            isActive={isActive}
                                            onFocus={this.handleFocusControl}
                                            onBlur={this.handleBlurControl}
                                            disabled={!canUserAccess}
                                            renderLabelPopover
                                            {...(nodeLabelTypeProps[nodeType] || nodeLabelTypeProps.default)}
                                        >
                                            {contentLabel}
                                        </LayoutNodeLabel>
                                        <FormEditorNodeControls
                                            canUserDelete={canUserDeleteRegistrationForm || canUserEditLibraryForms}
                                            isActive={isActive}
                                            hasLogic={hasLogic}
                                            editingLocked={editingLocked}
                                            nodeLockingAllowed={nodeLockingAllowed}
                                            hideAddQuestionButton={hideAddQuestionButton}
                                            onFocusControl={this.handleFocusControl}
                                            onBlurControl={this.handleBlurControl}
                                            nodeType={nodeType}
                                            {...(nodeControlTypeProps[nodeType] || nodeControlTypeProps.default)}
                                        />
                                    </>
                                ),
                            }[status]
                        }
                    </LayoutNodeContentContainer>
                )}
            </FormBuilderContext.Consumer>
        );
    }
}

const mapStateToProps = (state, props) => ({
    isAllowedLogicNode: isAllowedLogicNode(state, props.formNode),
    editingLocked: getFormNodeEditingLocked(state, props.formNode.id),
    nodeLockingAllowed: getNodeLockingAllowed(state),
    hideAddQuestionButton: getFormTabHideAddQuestionButton(state),
    canUserDeleteRegistrationForm: getCanUserDeleteRegistrationForm(state),
    canUserEditLibraryForms: getCanUserUpdateLibraryFormsUserPermission(state),
});

const mapDispatchToProps = {
    createTextElementContentNode,
    navigateToTicketEditor,
};

export default compose(withFlags, connect(mapStateToProps, mapDispatchToProps))(LayoutNodeContentComponent);
